import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CurrentUrlService, stripQueryParams } from '@cumlaude/shared-services';
import { RouteDef } from '@cumlaude/shared-components-menu';
import { Crumb, crumbPath, isDetailsPath, staticPath } from '../layout/paginakop/breadcrumb/breadcrumb-model';
import { UserRouteData, UserRoutesService } from './user-routes.service';

/**
 * Levert de zichtbare crumbs voor de paginakop-breadcrumb: het static path van elke bezochte
 * herkomstpagina (de trail), gevolgd door het static path van de huidige pagina.
 *
 * Eén step in de trail is één eerder bezochte herkomstpagina, bewaard als volledige url zodat een
 * klik de filterstate van die pagina exact terugbrengt. Eén step levert dus meerdere crumbs op:
 *
 *   steps: ['/aanwezigheid/statistieken?schooljaar=2025%2F2026']  ->  Aanwezigheid > Statistieken
 *   huidig pad: '/details/leerling'                               ->  Details > Leerling
 *
 * De trail wordt afgeleid uit navigatie, in een effect op CurrentUrlService.currentUrl(): verlengd
 * bij een doorklik naar een details/**-pagina (de `doorklik`-vlag op de navigatie-state), getruncate
 * bij het hervinden van een eerdere stap, en gewist bij elke andere padwijziging (bijv. een klik op
 * een hoofdmenu-item).
 *
 * `snapshots` bewaart per history-entry (Angular Router's `navigationId`) wat de trail op dat
 * moment was, zodat een browser-back die trail kan terugzetten in plaats van 'm te wissen. De
 * router geeft elke transitie -- ook een popstate -- een nieuw id; `NavigationStart.restoredState`
 * levert het oorspronkelijke id van de herbezochte entry, en elke geslaagde herstel-lookup slaat de
 * trail opnieuw op onder dat nieuwe id, zodat een volgende back naar diezelfde entry hem ook weer
 * vindt.
 *
 * Leeft alleen in memory, FIFO-begrensd op MAX_SNAPSHOTS -- overleeft navigeren binnen de app, niet
 * een paginareload.
 */
@Injectable({
	providedIn: 'root',
})
export class BreadcrumbService {
	private static readonly MAX_SNAPSHOTS = 200;

	private readonly routes = inject(UserRoutesService);
	private readonly currentUrl = inject(CurrentUrlService);
	private readonly router = inject(Router);

	private readonly steps = signal<readonly string[]>([]);
	private vorigeUrl: string | undefined;

	/**
	 * Laatst bekende trail per history-entry (Router's navigationId), voor back/forward-herstel.
	 */
	private readonly snapshots = new Map<number, readonly string[]>();

	/**
	 * Het navigationId waarmee de huidige entry ooit is gepusht, gevuld door NavigationStart.
	 */
	private restoredNavigationId: number | undefined;

	readonly crumbs = computed<Crumb[]>(() => {
		const routes = this.routes.activeAuthorisedRoutes();
		const huidigPad = this.currentUrl.currentPath();

		const stepCrumbs = this.steps().flatMap((stepUrl) => this.crumbsVoorStep(routes, stepUrl, false));
		const huidigeCrumbs = huidigPad ? this.crumbsVoorStep(routes, huidigPad, true) : [];

		return [...stepCrumbs, ...huidigeCrumbs];
	});

	constructor() {
		this.router.events
			.pipe(
				filter((e): e is NavigationStart => e instanceof NavigationStart),
				takeUntilDestroyed()
			)
			.subscribe((e) => {
				this.restoredNavigationId = e.restoredState?.navigationId;
			});

		effect(() => {
			const url = this.currentUrl.currentUrl();
			if (!url) return;

			// untracked: dit moet alleen op een navigatie (currentUrl) reageren, niet op de eigen
			// steps.set()-schrijven hieronder of op een rolwissel die activeAuthorisedRoutes() wijzigt
			// zonder dat er genavigeerd is.
			untracked(() => this.verwerkNavigatie(url, this.isDoorklik()));
		});
	}

	/**
	 * Het static path van `stepUrl`, met alleen de laatste crumb (de daadwerkelijk bezochte pagina
	 * van deze stap) als clickable -- alle voorgaande crumbs zijn menugroepen. Voor een trail-step
	 * (isHuidig=false) navigeert die laatste crumb naar de bewaarde volledige url (incl.
	 * queryparams) zodat filters/selectie exact terugkomen; voor de huidige pagina is hij nooit
	 * clickable, maar wel `current`.
	 */
	private crumbsVoorStep(routes: readonly RouteDef<UserRouteData>[], stepUrl: string, isHuidig: boolean): Crumb[] {
		const pad = staticPath(routes, stripQueryParams(stepUrl));

		return pad.map((crumb, i) => {
			if (i < pad.length - 1) return { label: crumb.label, clickable: false, current: false };
			if (isHuidig) return { label: crumb.label, clickable: false, current: true };

			return {
				label: crumb.label,
				url: crumb.url,
				queryParams: this.router.parseUrl(stepUrl).queryParams,
				clickable: true,
				current: false,
			};
		});
	}

	private verwerkNavigatie(url: string, doorklik: boolean) {
		const vorigeUrl = this.vorigeUrl;
		this.vorigeUrl = url;

		// currentUrl() emit alleen ná NavigationEnd, en Angular zet lastSuccessfulNavigation vlak
		// daarvoor al binnen dezelfde transitie -- dus die is hier nooit null.
		const navigationId = this.router.lastSuccessfulNavigation()!.id;

		const routes = this.routes.activeAuthorisedRoutes();
		const key = crumbPath(routes, stripQueryParams(url));
		const vorigeKey = vorigeUrl ? crumbPath(routes, stripQueryParams(vorigeUrl)) : undefined;

		const restoredNavigationId = this.restoredNavigationId;
		const snapshot = restoredNavigationId === undefined ? undefined : this.snapshots.get(restoredNavigationId);
		const nieuweSteps = key === vorigeKey ? this.steps() : (snapshot ?? this.berekenSteps(routes, key, vorigeUrl, vorigeKey, doorklik));

		this.steps.set(nieuweSteps);
		this.bewaarSnapshot(navigationId, nieuweSteps);
	}

	/**
	 * De trail voor een navigatie die niet uit een bekende snapshot kon worden hersteld: getruncate
	 * bij het hervinden van een eerdere stap, verlengd bij een doorklik naar een details/**-pagina,
	 * en anders gewist.
	 */
	private berekenSteps(
		routes: readonly RouteDef<UserRouteData>[],
		key: string,
		vorigeUrl: string | undefined,
		vorigeKey: string | undefined,
		doorklik: boolean
	): readonly string[] {
		const i = this.steps().findIndex((step) => crumbPath(routes, stripQueryParams(step)) === key);
		if (i >= 0) return this.steps().slice(0, i);

		if (!doorklik || !isDetailsPath(key)) return [];
		if (vorigeUrl && vorigeKey !== '/start') return [...this.steps(), vorigeUrl];
		return this.steps();
	}

	private bewaarSnapshot(navigationId: number, steps: readonly string[]) {
		this.snapshots.set(navigationId, steps);
		if (this.snapshots.size > BreadcrumbService.MAX_SNAPSHOTS) {
			this.snapshots.delete(this.snapshots.keys().next().value!);
		}
	}

	private isDoorklik(): boolean {
		return (<{ doorklik?: boolean } | undefined>this.router.lastSuccessfulNavigation()!.extras.state)?.doorklik === true;
	}
}
